import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import securityService from '@api/v1/securityService'
import { UsersSection } from '@pages/Settings'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { securityAccessFrom } from '@api/v1/securityAccess'

const TABS = [
  { id: 'profiles', label: 'Security Profiles' },
  { id: 'users', label: 'Users' },
]

const SecurityAdmin = () => {
  useDocumentTitle('Security')
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('profiles')
  const [selectedProfileId, setSelectedProfileId] = useState(null)

  /*
   * Spec 163. cb-api answers every /security route to Centerbase administrators only
   * (CB's own Get_IsUserAdmin, asked as this user). Ask that FIRST, and read nothing
   * else unless the answer is an explicit yes: a non-admin must see that this page is
   * for administrators, not an empty page and not a generic error. Not retried: a 403
   * is CB's answer, and a check CB could not answer gets a visible Try again instead
   * of a silent second attempt.
   */
  const accessQuery = useQuery({
    queryKey: ['security-access'],
    queryFn: () => securityService.getAccess(),
    retry: false,
  })
  const accessRead = useReadState('security-access', accessQuery)
  const access = securityAccessFrom(accessRead, accessQuery)
  const isAdmin = access === 'admin'

  const profilesQuery = useQuery({
    queryKey: ['security-profiles'],
    queryFn: () => securityService.getProfiles(),
    enabled: isAdmin,
  })
  const { data: profilesData } = profilesQuery
  /*
   * CC-53132. This page decides who can see what, so "there are no security profiles"
   * is a claim with teeth: a reader who believes it concludes the firm has no access
   * control configured. It was gated on `isLoading`, which is FALSE for a read that
   * never happened (a paused fetch calls queryFn zero times and throws nothing).
   *
   * `idle` is only reachable before the access check says yes (spec 163), and this
   * panel is not rendered until it does, so it is folded into the loading branch. That
   * fold is deliberate rather than incidental: an unhandled fifth state falls through
   * every branch and renders a blank panel.
   */
  const profilesState = useReadState('security-profiles', profilesQuery)
  /*
   * The SHAPE half too (`readShape.js`'s sibling failure): `profilesData?.data || []`
   * rewrote a body we could not read as "no profiles" just as silently as a paused
   * read did. A resolved read whose payload is not an array is a refusal, not a zero.
   */
  const profilesShapeOk = Array.isArray(profilesQuery.data?.data)
  const profilesReady = profilesState === 'ready' && profilesShapeOk
  const profilesUnreadable =
    profilesState === 'blocked' || profilesState === 'failed' || (profilesState === 'ready' && !profilesShapeOk)

  const detailQuery = useQuery({
    queryKey: ['security-profile', selectedProfileId],
    queryFn: () => securityService.getProfile(selectedProfileId),
    enabled: isAdmin && !!selectedProfileId,
  })
  const { data: profileDetail } = detailQuery
  /*
   * `idle` IS reachable on this one — the query is disabled until a profile is picked
   * — and it must NOT be folded into loading or surfaced as a failure. The panel
   * already answers that case with "Select a security profile…", and folding idle in
   * would replace that with a spinner that never resolves.
   */
  const detailState = useReadState('security-profile', detailQuery)
  const detailUnreadable = detailState === 'blocked' || detailState === 'failed'

  const groupsQuery = useQuery({
    queryKey: ['security-groups'],
    queryFn: () => securityService.getGroups(),
    enabled: isAdmin,
  })
  const { data: groupsData } = groupsQuery
  /*
   * The quietest of the three. This section is rendered behind `groups.length > 0`,
   * so an unresolved read did not print a false zero — it removed the whole "Security
   * Groups" card from the page. Absence of a section reads as "this firm has no
   * groups" just as confidently as a sentence saying so, and it is harder to notice
   * because there is nothing on screen to disbelieve. `idle` is only reachable before
   * the access check says yes, when none of this renders, so it folds into the
   * not-yet-ready case.
   */
  const groupsState = useReadState('security-groups', groupsQuery)
  const groupsUnreadable = groupsState === 'blocked' || groupsState === 'failed'
  const groupsReady = groupsState === 'ready' && Array.isArray(groupsData?.data)

  const deleteMutation = useMutation({
    mutationFn: (id) => securityService.deleteProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security-profiles'] })
      setSelectedProfileId(null)
    },
  })

  const profiles = profilesData?.data || []
  const groups = groupsData?.data || []
  const detail = profileDetail?.data

  const heading = (
    <div className="mb-6">
      <h1 className="text-2xl font-display text-gray-900">Security & Permissions</h1>
      <p className="text-sm text-gray-500 mt-1">Manage security profiles, users, groups, and permissions</p>
    </div>
  )

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto">
        {heading}
        <SecurityAccessPanel access={access} query={accessQuery} />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {heading}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit" role="tablist" aria-label="Security tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && <UsersSection />}

      {activeTab === 'profiles' && <><div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Profiles List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Security Profiles</h2>
            <p className="text-xs text-gray-500 mt-0.5">Click a profile to view permissions</p>
          </div>
          <div className="divide-y divide-gray-50">
            {profilesUnreadable ? (
              <div role="alert" className="p-6 text-center">
                <p className="text-sm font-semibold text-gray-900">
                  {profilesState === 'blocked'
                    ? 'Can’t reach the server, so we haven’t loaded your security profiles.'
                    : 'We couldn’t load your security profiles.'}
                </p>
                {profilesState === 'failed' && profilesQuery.error?.message && (
                  <p className="mx-auto mt-1.5 max-w-sm text-[12.5px] text-gray-500">
                    {profilesQuery.error.message}
                  </p>
                )}
                <p className="mx-auto mt-1.5 max-w-sm text-[12.5px] text-gray-500">
                  {profilesState === 'ready'
                    ? 'This is not an empty set of profiles — the list came back unreadable.'
                    : 'This is not a firm with no security profiles — the list never arrived.'}
                </p>
                <button
                  type="button"
                  onClick={() => profilesQuery.refetch()}
                  className="btn-cb-toolbar mt-3.5"
                >
                  Try again
                </button>
              </div>
            ) : !profilesReady ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner message="Loading profiles..." />
              </div>
            ) : profiles.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 text-center">No security profiles found</p>
            ) : (
              profiles.map((profile) => (
                <button
                  key={profile.id}
                  onClick={() => setSelectedProfileId(profile.id)}
                  className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors ${
                    selectedProfileId === profile.id ? 'bg-cb-light border-l-2 border-cb-accent' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{profile.name}</p>
                    {profile.isAdmin && (
                      <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">Admin</span>
                    )}
                  </div>
                  {profile.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{profile.description}</p>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Profile Detail / Permissions */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {detail ? (detail.Name || detail.name || 'Profile Details') : 'Select a Profile'}
              </h2>
              {detail?.Description && (
                <p className="text-xs text-gray-500 mt-0.5">{detail.Description}</p>
              )}
            </div>
            {selectedProfileId && (
              <button
                onClick={() => {
                  if (confirm('Delete this profile?')) {
                    deleteMutation.mutate(selectedProfileId)
                  }
                }}
                className="text-xs text-red-500 hover:text-red-700 font-medium"
              >
                Delete Profile
              </button>
            )}
          </div>

          <div className="p-5">
            {!selectedProfileId ? (
              <p className="text-sm text-gray-500 text-center py-8">
                Select a security profile from the list to view its permissions
              </p>
            ) : detailUnreadable ? (
              /* Was a flat "Could not load profile details" with no retry, reached by
                 falling off the end of the chain — so it also caught the paused read,
                 where nothing had been attempted and there was nothing to report. Name
                 which of the two happened, and offer the read again. */
              <div role="alert" className="py-8 text-center">
                <p className="text-sm font-semibold text-gray-900">
                  {detailState === 'blocked'
                    ? 'Can’t reach the server, so we haven’t loaded this profile’s permissions.'
                    : 'We haven’t loaded this profile’s permissions.'}
                </p>
                <p className="mx-auto mt-1.5 max-w-sm text-[12.5px] text-gray-500">
                  This is not a profile with no permissions — nothing was read.
                </p>
                <button
                  type="button"
                  onClick={() => detailQuery.refetch()}
                  className="btn-cb-toolbar mt-3.5"
                >
                  Try again
                </button>
              </div>
            ) : detailState !== 'ready' ? (
              <div className="py-8 flex justify-center">
                <LoadingSpinner message="Loading permissions..." />
              </div>
            ) : detail ? (
              <PermissionsView detail={detail} />
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                Could not load profile details
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Security Groups */}
      {groupsUnreadable && (
        <div role="alert" className="mt-6 rounded-xl border border-gray-100 bg-white p-6 text-center shadow-sm">
          <p className="text-sm font-semibold text-gray-900">
            {groupsState === 'blocked'
              ? 'Can’t reach the server, so we haven’t loaded your security groups.'
              : 'We haven’t loaded your security groups.'}
          </p>
          <p className="mx-auto mt-1.5 max-w-sm text-[12.5px] text-gray-500">
            This is not a firm with no security groups — the list never arrived.
          </p>
          <button type="button" onClick={() => groupsQuery.refetch()} className="btn-cb-toolbar mt-3.5">
            Try again
          </button>
        </div>
      )}
      {groupsReady && groups.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Security Groups</h2>
            <p className="text-xs text-gray-500 mt-0.5">{groups.length} groups configured</p>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100"
                >
                  <p className="text-sm font-medium text-gray-900">{group.name}</p>
                  {group.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{group.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  )
}

const PermissionsView = ({ detail }) => {
  const permissions = detail.permissions || []
  const users = (detail.users || []).filter((u) => u.hasProfile)
  const groups = (detail.groups || []).filter((g) => g.assigned)

  return (
    <div className="space-y-6">
      {/* Assigned Users */}
      {users.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Assigned Users ({users.length})</h3>
          <div className="flex flex-wrap gap-2">
            {users.map((u) => (
              <span key={u.id} className="inline-flex px-2.5 py-1 rounded-full bg-cb-light text-xs text-cb-navy font-medium">
                {u.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Assigned Groups */}
      {groups.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Assigned Groups ({groups.length})</h3>
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => (
              <span key={g.id} className="inline-flex px-2.5 py-1 rounded-full bg-green-50 text-xs text-green-700 font-medium">
                {g.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Feature Permissions */}
      {permissions.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Permissions ({permissions.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Permission</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Enabled</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {permissions.map((p, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="py-2 pr-4 text-gray-900">
                      {p.SubKey || p.Key || p.Name || `Permission ${i}`}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {permIcon(p.Value ?? p.Enabled ?? p.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fallback: raw data */}
      {permissions.length === 0 && users.length === 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Profile Data</h3>
          <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 overflow-x-auto max-h-96">
            {JSON.stringify(detail, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

/*
 * Spec 163 — what the page shows until Centerbase has said this user is an
 * administrator. Four cases, never merged: still checking; CB said no (the only case
 * that may say "only administrators", and it offers no retry because nothing failed);
 * CB could not answer, or answered something that is not a yes; the check was never
 * sent (offline). None of them reads, or implies, anything about the firm's settings.
 */
const SecurityAccessPanel = ({ access, query }) => {
  if (access === 'checking') {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner message="Checking your access..." />
      </div>
    )
  }

  if (access === 'not-admin') {
    return (
      <div
        role="status"
        data-testid="security-admins-only"
        className="rounded-xl border border-gray-100 bg-white p-8 text-center shadow-sm"
      >
        <h2 className="text-base font-semibold text-gray-900">Only administrators can see security settings</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
          Security profiles, groups and permissions are limited to Centerbase administrators, as they are in
          Centerbase itself. If you need a change, ask an administrator at your firm.
        </p>
      </div>
    )
  }

  const serverSentence = query.error?.response?.data?.error
  return (
    <div
      role="alert"
      data-testid="security-access-unconfirmed"
      className="rounded-xl border border-gray-100 bg-white p-6 text-center shadow-sm"
    >
      <p className="text-sm font-semibold text-gray-900">
        {access === 'blocked'
          ? 'Can’t reach the server, so we haven’t checked whether you’re an administrator.'
          : 'We couldn’t confirm that you’re an administrator, so nothing on this page was loaded.'}
      </p>
      {access !== 'blocked' && typeof serverSentence === 'string' && serverSentence && (
        <p className="mx-auto mt-1.5 max-w-md text-[12.5px] text-gray-600">{serverSentence}</p>
      )}
      <p className="mx-auto mt-1.5 max-w-md text-[12.5px] text-gray-600">
        This is not a firm with no security settings — they were not read.
      </p>
      <button type="button" onClick={() => query.refetch()} className="btn-cb-toolbar mt-3.5">
        Try again
      </button>
    </div>
  )
}

const permIcon = (val) => {
  if (val === true || val === 'true' || val === 1) {
    return <span className="inline-block w-4 h-4 rounded-full bg-green-500" title="Allowed" />
  }
  if (val === false || val === 'false' || val === 0) {
    return <span className="inline-block w-4 h-4 rounded-full bg-gray-200" title="Denied" />
  }
  return <span className="text-xs text-gray-400">{String(val ?? '—')}</span>
}

const formatPermKey = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim()

export default SecurityAdmin
